import { ChangeEvent, FocusEvent } from 'react'
import { SelectChangeEvent } from '@mui/material'
import memoize from 'fast-memoize'
import * as R from 'ramda'
import scrollIntoView from 'scroll-into-view-if-needed'

import { Environment, Region } from '../constants/environment'
import i18n from '../locales/i18n'
import { AtLeast, BaseUser, Constant, IdObject, Nil } from '../types'

export function handleFormSelectInput(proceed: (value: any) => void) {
  return (arg: SelectChangeEvent<unknown>) => {
    if (Array.isArray(arg)) {
      proceed(arg)
      return
    }
    proceed(arg?.target?.value)
  }
}

function trimIfOnlySpaces(str: string) {
  return str.trim().length === 0 ? str.trim() : str
}

export function handleFormTextInput(proceed: (value: any) => void) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value = '', type } = {} } = event
    const cleanValue = trimIfOnlySpaces(value.toString())
    if (type === 'email') {
      proceed(cleanValue.trim())
      return
    }
    proceed(cleanValue)
  }
}

export function handleFormNumberInput(proceed: (value: any) => void) {
  return (event: ChangeEvent<HTMLInputElement>) =>
    proceed(Number(event?.target?.value ?? 0))
}

export function handleFormCheckboxInput(proceed: (value: any) => void) {
  return (event: ChangeEvent<HTMLInputElement>) => proceed(event.target.checked)
}

export function moveCursorToFirstValidPosition(input: HTMLInputElement) {
  const currentPosition = input.selectionStart ?? 0
  const firstEmptySpace = input.value.indexOf('\u2000')
  if (firstEmptySpace !== -1 && firstEmptySpace < currentPosition) {
    input.setSelectionRange(firstEmptySpace, firstEmptySpace)
  }
}

export function handleMaskInputFocus(
  handleFocus?: (event: FocusEvent<HTMLInputElement>) => void,
) {
  return (event: FocusEvent<HTMLInputElement>) => {
    const input = event.target
    setTimeout(() => moveCursorToFirstValidPosition(input))
    if (handleFocus) {
      handleFocus(event)
    }
  }
}

export function isDigit(char: string) {
  return char >= '0' && char <= '9'
}

export const clean = (object: any): any =>
  R.pickBy(value => typeof value !== 'undefined', object)

export function secondLevelMerge(mapA: any = {}, mapB: any = {}): object {
  return R.mergeAll(
    Object.keys({ ...mapA, ...mapB }).map(key => ({
      [key]: R.mergeRight(mapA[key], clean(mapB[key])),
    })),
  )
}

export function scrollNodeIntoViewIfNeeded(node: Element) {
  if (node.scrollIntoView) {
    node.scrollIntoView()
  } else {
    scrollIntoView(node, { scrollMode: 'if-needed' })
  }
}

export function findByProp(
  name: string,
  list: any[] | undefined,
  prop: string,
) {
  return (list || []).find(item => item[prop] === name)
}

export function findMultipleByProp(
  name: string,
  list: any[] | undefined,
  prop: string,
) {
  return (list || []).filter(item => item[prop] === name)
}

export function findById(id: string | Nil, list: any[]) {
  if (!id) {
    return undefined
  }
  return findByProp(id, list, 'id')
}

export const getConstantsValue = memoize(findById)

export function getConstantName(
  id: string | Nil,
  list: Constant[],
  defValue?: string,
) {
  const constant = getConstantsValue(id, list)
  return constant ? constant.name : defValue
}

export const enumToSelectableList = (obj: object) =>
  Object.values(obj).map(value => ({ id: value, name: value }))

export function getSpayedNeutered(gender: string, Gender: Constant[]) {
  return getConstantName(gender, Gender, '').toLowerCase() === 'female'
    ? i18n.t('Utils:PET_SPAYED')
    : i18n.t('Utils:PET_NEUTERED')
}

export function isLocalEnvironment() {
  const { hostname } = window.location
  return hostname.includes('localhost') || hostname.includes('127.0.0.1')
}

export function isPlayEnvironment() {
  const { hostname } = window.location
  return (
    (hostname.includes('play.') || hostname.includes('master.')) &&
    !isLocalEnvironment()
  )
}

export const isStaging = () => {
  const { hostname } = window.location
  return hostname.includes('stage.')
}

export const isProduction = () => {
  const { hostname } = window.location
  return !hostname.includes('stage.') && !isLocalEnvironment()
}

export const getEnvironmentName = R.cond([
  [isPlayEnvironment, R.always('Play')],
  [isProduction, R.always('Production')],
  [isStaging, R.always('Staging')],
  [R.T, R.always('Local')],
])

export const isEuropeRegion = () => {
  const { hostname } = window.location
  return hostname.includes('eu.')
}

export const getDomainContext = () => ({
  region: isEuropeRegion() ? Region.EU : Region.US,
  env: R.cond([
    [isPlayEnvironment, R.always(Environment.MASTER)],
    [isProduction, R.always(Environment.PROD)],
    [isStaging, R.always(Environment.STAGE)],
    [R.T, R.always(Environment.LOCAL)],
  ])(),
})

export const isTooltip = (element: HTMLElement | null) => {
  let curElement = element
  while (curElement && curElement.tagName.toLowerCase() !== 'body') {
    if (curElement.getAttribute('role') === 'tooltip') {
      return true
    }
    curElement = curElement.parentElement
  }
  return false
}

// Check if all conditions are match
export const matchAllConditions = (conditionArray: boolean[]) =>
  Array.isArray(conditionArray) && !conditionArray.includes(false)

export function findByName(name: string, list: any[]) {
  return findByProp(name, list, 'name')
}

export function findMultipleByName(name: string, list: any[]) {
  return findMultipleByProp(name, list, 'name')
}

export const findConstantByName = memoize(findByName)

export const findConstantsByName = memoize(findMultipleByName)

export function findConstantIdByName(name: string, constantList: Constant[]) {
  const constant = findConstantByName(name, constantList)
  return constant && constant.id
}

export function findConstantIdsByName(name: string, constantList: Constant[]) {
  const constants: Constant[] = findConstantsByName(name, constantList)
  return constants.length > 0 ? R.pluck('id', constants) : []
}

export const removeByIds = <T extends IdObject>(
  itemIds: string[],
  list: T[],
): T[] => R.filter(({ id }) => !R.includes(id, itemIds), list)

export const removeById = <T extends IdObject>(
  itemId: string,
  list: T[],
): T[] => removeByIds([itemId], list)

export const updateByIds = <T extends IdObject>(
  newItems: T[],
  list?: T[],
): T[] =>
    R.map(item => R.find(R.propEq('id', item.id), newItems) || item, list || [])

export const updateById = <T extends IdObject>(newItem: T, list: T[]): T[] =>
  updateByIds([newItem], list)

export const partialUpdateByIds = <T extends IdObject>(
  newItems: AtLeast<T, 'id'>[],
  list?: T[],
): T[] =>
    R.map(item => {
      const foundItemById = R.find(R.propEq('id', item.id), newItems)
      return foundItemById ? R.mergeRight<any, any>(item, foundItemById) : item
    }, list || [])

export const partialUpdateById = <T extends IdObject>(
  newItem: AtLeast<T, 'id'>,
  list: T[],
): T[] => partialUpdateByIds([newItem], list)

export const toggleListItem = R.curry((value, list) =>
  R.ifElse(R.includes(value), R.without([value]), R.append(value))(list),
)

export function toExternalUrl(url?: string) {
  if (url && !url.match(/^https?:\/\/.*$/)) {
    return `http://${url}`
  }

  return url
}

export function getUrlHost(url?: string) {
  try {
    const urlWithProtocol =
      url && !url.match(/^https?:\/\/.*$/) ? `http://${url}` : url || ''

    const urlObj = new URL(urlWithProtocol)

    return urlObj.host
  } catch (err) {
    return url
  }
}

export function getPersonString(person: BaseUser | Nil) {
  const { firstName, lastName } = person || {}
  return `${firstName || ''} ${lastName || ''}`.trim()
}

export const loadScript = (
  id: string,
  src: string,
  isAsync: boolean,
  listeners = [],
) => {
  const script = document.createElement('script')
  script.src = src
  script.async = isAsync
  script.id = id
  listeners.forEach(({ event, eventHandler }) => {
    script.addEventListener(event, eventHandler)
  })
  return script
}

export function round(x?: number | null, base = 1) {
  if (R.isNil(x)) {
    return x
  }
  const preparedMultipliedValue = Number((x * 10 ** base).toFixed(2))
  return Math.round(preparedMultipliedValue) / 10 ** base
}

export const sleep = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms)
  })

export const callWithRetry = async (
  callback: () => boolean | void,
  retries = 4,
  delay = 300,
) => {
  if (!callback) {
    return
  }
  while (retries-- > 0) {
    if (callback()) {
      return
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(delay)
  }
}

export const resolveValueOrGetter = (item: any, valueOrGetter: any) =>
  typeof valueOrGetter === 'function' ? valueOrGetter(item) : valueOrGetter

export const isPuiIdentifier = (string?: string) =>
  typeof string === 'string' &&
  string.length === 8 &&
  Number(/[A-Z]/.test(string)) +
    Number(/[a-z]/.test(string)) +
    Number(/\d/.test(string)) >=
    2

export function openLink(properties: object) {
  Object.assign(document.createElement('a'), properties).click()
}

export function downloadLink(properties: object) {
  openLink({ download: '', ...properties })
}

export function openInNewTab(properties: object) {
  openLink({ target: '_blank', ...properties })
}

export const omitTranslationProps = (rest: object) => R.omit(['tReady'], rest)

export const matchSubstring = (substring: string, string: string) =>
  string.toLowerCase().includes(substring.toLowerCase())

export const normalizeConstantWithOthers = (constant: Constant[]) =>
  R.map(
    R.when(
      R.propEq<string>('nameTranslation', 'Other'),
      R.assoc('nameTranslation', i18n.t('Common:OTHER')),
    ),
    constant,
  ) as Constant[]

export const textWillOverflow = (e: HTMLElement | null) =>
  e ? e.offsetWidth < e.scrollWidth : false

export function resizeArray(arr: any[], newSize: number, defaultValue?: any) {
  return [
    ...arr,
    ...Array(Math.max(newSize - arr.length, 0)).fill(defaultValue),
  ]
}

export const getMatchForHighlighting = (
  highlights: string[],
  text: string,
  completeMatch?: boolean,
) => {
  const highlightedWords = text.split(' ').reduce((acc, item) => {
    const matchedHighlight = completeMatch
      ? highlights.find(highlight => item.includes(highlight))
      : highlights.find(highlight =>
        item.toLocaleLowerCase().includes(highlight.toLocaleLowerCase()),
      )

    return matchedHighlight ? [...acc, matchedHighlight] : acc
  }, [] as string[])

  return highlightedWords.join(' ')
}

export const getHighlightedText = (
  text: string | undefined,
  highlights?: string[] | Nil,
  completeMatch?: boolean,
) => {
  if (!text) {
    return ''
  }

  const validHighlights = (highlights || []).filter(Boolean)
  return getMatchForHighlighting(validHighlights, text, completeMatch)
}
